/**
 * Custom hook that wraps useApiClient with automatic API version detection
 * Detects API version based on URL parameter (accesstoken vs token + context params)
 */

import { useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { type ApiClient, useApiClient } from "@/api";
import {
	getApiVersionFromSearchParams,
	getAuthTokenFromSearchParams,
	validateRampsSearch,
} from "@/lib/ramps-search-schema";
import { rampsActions, useRampsStore } from "@/stores/use-ramps-store";
import { extractWalletAddressFromJwt } from "@/utils/jwt-decoder";

interface UseApiClientOptions {
	authToken?: string;
	walletAddress?: string;
	isStandaloneTransactionStatus?: boolean;
}

/**
 * Hook that automatically detects API version from URL parameters and creates API client
 * - accesstoken parameter → v5 API (OAuth, wallet address as query param)
 * - token + URL context params → v2 API (JWT, context from URL)
 * - token without URL context → v3 API (JWT, context from prepare endpoint)
 * - Detects synchronously from URL params, falling back to store if needed
 */
export function useApiClientWithAutoVersion(
	options?: Partial<UseApiClientOptions>,
): { apiClient: ApiClient; apiVersion: string; walletAddress: string | null } {
	const store = useRampsStore();
	const search = useSearch({ strict: false });

	// Get auth token from options, URL params, or store (in priority order)
	const authToken =
		options?.authToken ||
		getAuthTokenFromSearchParams(validateRampsSearch(search)) ||
		store.token;

	// Synchronously detect API version from URL params first
	// This ensures we get the correct version immediately, even if store isn't updated yet
	const validatedSearch = validateRampsSearch(search);
	const isStandaloneTransactionStatus =
		options?.isStandaloneTransactionStatus ??
		(window.location.pathname.includes("/transaction-status") &&
			!!validatedSearch.token &&
			!validatedSearch.accesstoken &&
			!(
				validatedSearch.operation &&
				validatedSearch.wallet_provider &&
				validatedSearch.asset_code
			) &&
			!validatedSearch.refNumber);

	// Detect version synchronously from URL params
	const detectedVersionFromUrl = getApiVersionFromSearchParams(
		validatedSearch,
		isStandaloneTransactionStatus,
	);

	// Determine API version using explicit priority:
	// 1. Store version (if exists, same token, and URL doesn't have conflicting v2 indicators)
	//    Preserves flow continuity (e.g., v3 from transaction-status to cancel)
	// 2. URL detection - use when store doesn't have version or has conflicting indicators
	// 3. Default to v3 only if no token and no store version (last resort)
	let apiVersion: "v2" | "v3" | "v5";

	// Check if URL has v2 indicators that might conflict with store version
	const hasV2IndicatorsInUrl =
		!!validatedSearch.refNumber ||
		!!(
			validatedSearch.operation &&
			validatedSearch.wallet_provider &&
			validatedSearch.asset_code
		);

	// Preserve store version if:
	// - Store has version AND
	// - Same token AND
	// - Either: no v2 indicators in URL, OR store version is v3 (v3 should be preserved even with refNumber)
	const shouldPreserveStoreVersion =
		store.apiVersion &&
		authToken === store.token &&
		(!hasV2IndicatorsInUrl || store.apiVersion === "v3");

	if (shouldPreserveStoreVersion) {
		// Store has version for same token - preserve it to maintain flow continuity
		// v3 is preserved even if refNumber is in URL (refNumber might be for API, not v2 indicator)
		apiVersion = store.apiVersion;
	} else if (detectedVersionFromUrl !== null) {
		// URL detection returned explicit version (v2/v3/v5)
		// Use URL detection when store doesn't have version or has conflicting indicators
		apiVersion = detectedVersionFromUrl;
	} else {
		// No token in URL and no store version - default to v3 (backward compatibility)
		apiVersion = "v3";
	}

	// Sync detected version to store (if different) to keep store in sync
	// This ensures other components reading from store get the correct version
	// Use ref to track last synced version to prevent multiple updates
	const lastSyncedVersion = useRef<string | null>(null);
	const lastAuthToken = useRef<string | null>(null);

	useEffect(() => {
		// Only sync if:
		// 1. Version is different from store
		// 2. We haven't already synced this version for this token
		// 3. Token hasn't changed (if token changes, reset tracking)
		if (authToken !== lastAuthToken.current) {
			lastAuthToken.current = authToken;
			lastSyncedVersion.current = null; // Reset on token change
		}

		if (
			apiVersion &&
			apiVersion !== store.apiVersion &&
			apiVersion !== lastSyncedVersion.current
		) {
			lastSyncedVersion.current = apiVersion;
			rampsActions.setApiVersion(apiVersion);
		}
	}, [apiVersion, store.apiVersion, authToken]);

	// Extract wallet address based on API version
	// V5 (OAuth): Use wallet address from options/store (passed as query param)
	// V2/V3 (JWT): Extract wallet address from JWT token's 'sub' claim
	let walletAddress: string | null = null;

	if (apiVersion === "v5") {
		// OAuth flow - wallet address comes from external source (URL param or store)
		walletAddress = options?.walletAddress || store.walletAddress || null;
	} else {
		// JWT flow (v2/v3) - extract wallet address from token
		// Only attempt extraction if we have a token
		if (authToken) {
			walletAddress = extractWalletAddressFromJwt(authToken);
		}
	}

	const apiClient = useApiClient({
		authToken,
		apiVersion,
		walletAddress: walletAddress || undefined,
		transactionId: store.transactionId || undefined,
	});

	return {
		apiClient,
		apiVersion,
		walletAddress,
	};
}
